import { ApiSanctionEntityType, ApiScreeningDirection, ApiScreeningTest } from '../../api';
import React, { useEffect, useMemo, useState } from 'react';
import { screeningTestApi } from '../../apis';
import ScreeningTestModal, { ScreeningTestModalValues } from './ScreeningTestModal';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { IconColor, SalvButton, SalvTable, Size } from '@salvapp/ui-components';
import SalvPage from '../../common/SalvPage';
import { useDispatch } from 'react-redux';
import './ScreeningTests.scss';
import { createColumnHelper } from '@tanstack/react-table';
import { showFeedback } from '../../feedback/feedbackReducer';
import Has from '../../auth/Has';
import { CAN_DELETE_SCREENING_TEST } from '../../auth/permissions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';
import { getInverseScreeningValues } from './inverse';

const ScreeningTests = () => {
  const dispatch = useDispatch();
  const { features } = useMe();
  const inverseEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DAILY_SCREENING_PERSON_SCREENABLE_VALUES, features);
  const [screeningTests, setScreeningTests] = useState<ApiScreeningTest[]>([]);

  const fetchScreeningTests = async () => {
    const screeningTests = await screeningTestApi.findScreeningTests();
    setScreeningTests(screeningTests);
  };

  const createScreeningTest = async (values: ScreeningTestModalValues) => {
    // @ts-ignore
    const entityType = ApiSanctionEntityType[values.entityType.value];
    const isInverse = values.screeningDirection === ApiScreeningDirection.INVERSE;
    const { screeningListType, customListCode, recordExternalIds } = getInverseScreeningValues(values);

    const apiScreeningTest: ApiScreeningTest = {
      minId: isInverse ? undefined : values.minId,
      maxId: isInverse ? undefined : values.maxId,
      entityType: entityType,
      screenableFields: values.screenableFields ? JSON.parse(values.screenableFields) : undefined,
      selectorGroupCodes: isInverse
        ? undefined
        : values.selectorGroupCodes?.map((selectorGroupCode) => selectorGroupCode.value),
      clearanceRules: values.clearanceRules ? JSON.parse(values.clearanceRules) : undefined,
      goodlistEnabled: values.goodlistEnabled,
      useExistingHits: values.useExistingHits,
      scoreThreshold: isInverse ? undefined : values.scoreThreshold,
      bicLengthThreshold: isInverse ? undefined : values.bicLengthThreshold,
      applicationFeatures: values.applicationFeatures ? JSON.parse(values.applicationFeatures) : undefined,
      useOnlyScreenedEntities: isInverse ? undefined : values.useOnlyScreenedEntities,
      exemptionRulesEnabled: values.exemptionRulesEnabled,
      screeningDirection: isInverse ? ApiScreeningDirection.INVERSE : ApiScreeningDirection.REGULAR,
      screeningListType,
      customListCode,
      recordExternalIds,
    };
    await screeningTestApi.createScreeningTest({ apiScreeningTest });
    await fetchScreeningTests();
  };

  const cancelTest = async (id: number) => {
    await screeningTestApi.cancelScreeningTest({ id });
    dispatch(
      showConfirmationModal({
        title: 'Cancelled!',
        description: 'Test has been cancelled. It takes a bit to take effect.',
        confirmationText: 'Ok',
        icon: faCircleCheck,
        iconColor: IconColor.SUCCESS,
      })
    ).then(async () => {
      await fetchScreeningTests();
    });
  };

  const scheduleTest = async (id: number) => {
    await screeningTestApi.scheduleScreeningTest({ id });

    dispatch(
      showConfirmationModal({
        title: 'Scheduled!',
        description: 'Test has been scheduled for a run. It takes a bit to take effect.',
        confirmationText: 'Ok',
        icon: faCircleCheck,
        iconColor: IconColor.SUCCESS,
      })
    ).then(async () => {
      await fetchScreeningTests();
    });
  };

  useEffect(() => {
    fetchScreeningTests();
  }, []);

  const columnHelper = createColumnHelper<ApiScreeningTest>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
      }),
      columnHelper.accessor('entityType', {
        header: 'Entity type',
      }),
      ...(inverseEnabled
        ? [
            columnHelper.accessor('screeningDirection', {
              header: 'Direction',
            }),
          ]
        : []),
      columnHelper.accessor('minId', {
        header: 'From ID',
      }),
      columnHelper.accessor('maxId', {
        header: 'To ID',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row: { original: screeningTest } }) =>
          screeningTest.status +
          (screeningTest.status === 'RUNNING' && screeningTest.screeningDirection === ApiScreeningDirection.REGULAR
            ? ' - ' + screeningTest.currentId
            : ''),
      }),
      columnHelper.display({
        header: 'Actions',
        cell: ({ row: { original: screeningTest } }) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            <ScreeningTestModal test={screeningTest} />
            <ScreeningTestModal test={screeningTest} onSubmit={createScreeningTest} />
            {(screeningTest.status === 'RUNNING' || screeningTest.status === 'SCHEDULED') && (
              <SalvButton
                label={'Cancel'}
                variant='danger'
                size={Size.SMALL}
                onClick={() => cancelTest(screeningTest.id!)}
              />
            )}
            {screeningTest.status === 'NEW' && (
              <SalvButton
                label={'Run'}
                variant={'primary'}
                size={Size.SMALL}
                onClick={() => scheduleTest(screeningTest.id!)}
              />
            )}
            {screeningTest.status === 'FAILED' && (
              <SalvButton
                label={'Reschedule'}
                variant={'danger'}
                size={Size.SMALL}
                onClick={() => scheduleTest(screeningTest.id!)}
              />
            )}
          </div>
        ),
      }),
    ],
    [inverseEnabled]
  );

  const confirmDeletion = () => {
    dispatch(
      showConfirmationModal({
        title: 'Delete all tests',
        description: 'Are you sure you want to delete all screening tests?',
        confirmationText: 'Delete',
        iconColor: IconColor.DANGER,
        onConfirm: async () => {
          await screeningTestApi.deleteScreeningTests();
          dispatch(showFeedback({ message: 'Deletion scheduled', variant: 'success' }));
        },
      })
    );
  };

  return (
    <SalvPage
      title={'Screening tests'}
      actions={
        <>
          <ScreeningTestModal onSubmit={createScreeningTest} />
          <Has allPermissions={CAN_DELETE_SCREENING_TEST}>
            <SalvButton label={'Delete all tests'} onClick={confirmDeletion} variant={'danger'} size={Size.SMALL} />
          </Has>
        </>
      }
    >
      <SalvTable columns={columns} data={screeningTests} emptyMessage={'No screening tests have been defined'} />
    </SalvPage>
  );
};

export default ScreeningTests;
